'use client';

import { useState } from 'react';
import { DEFAULT_ACCEPT_ALL, DEFAULT_REJECT, type ConsentChoices } from '@/lib/consent';
import { useConsent } from '@/lib/hooks/use-consent';
import { getCopy } from '@/lib/copy';

const R = '/_auto/components/cookie-consent-banner';

export function CookieConsentBanner() {
  const { mode, setMode, gpcSignal, persistConsent } = useConsent();
  const [choices, setChoices] = useState<ConsentChoices>(DEFAULT_REJECT);

  const onAcceptAll = () => persistConsent(DEFAULT_ACCEPT_ALL);
  const onRejectAll = () => persistConsent(DEFAULT_REJECT);
  const onSavePreferences = () => persistConsent(choices);

  if (mode === 'hidden') return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={getCopy(R, 'cookiePreferences')}
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[100] border-t border-slate-200 bg-white shadow-[0_-10px_40px_rgba(0,0,0,0.08)]"
    >
      <div className="mx-auto max-w-6xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] md:p-6">
        {mode === 'banner' && (
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="min-w-0 flex-1 text-sm leading-relaxed text-slate-700">
              <p className="mb-1 font-semibold text-slate-900">{getCopy(R, 'weValueYourPrivacy')}</p>
              <p>
                {getCopy(R, 'pureboxUsesAMinimalSetOfEssentialCookiesToKeepYou-')} <strong>not</strong>{' '}
                {getCopy(R, 'setAdvertisingCookiesOptionalBucketsAnalyticsPreferenc')}
                {gpcSignal && (
                  <span className="mt-1 block text-slate-500">
                    {getCopy(R, 'yourBrowserSentAGlobalPrivacyControlSignalNonEssent')}
                  </span>
                )}{' '}
                {getCopy(R, 'seeOur')}{' '}
                <a href="/privacy" className="text-blue-700 underline">
                  {getCopy(R, 'privacyPolicy')}
                </a>
                .
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <button type="button" onClick={() => setMode('preferences')} data-testid="consent-preferences-button" className="min-h-11 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50">
                {getCopy(R, 'preferences')}
              </button>
              <button type="button" onClick={onRejectAll} data-testid="consent-reject-button" className="min-h-11 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50">
                {getCopy(R, 'rejectNonEssential')}
              </button>
              <button type="button" onClick={onAcceptAll} data-testid="consent-accept-button" className="min-h-11 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700">
                {getCopy(R, 'acceptAll')}
              </button>
            </div>
          </div>
        )}

        {mode === 'preferences' && (
          <div className="space-y-4" data-testid="consent-preferences-panel">
            <div>
              <p className="mb-1 font-semibold text-slate-900">{getCopy(R, 'cookiePreferences2')}</p>
              <p className="text-sm text-slate-600">
                {getCopy(R, 'youCanEnableOrDisableEachCategoryBelowEssentialCook')}
              </p>
            </div>
            <ul className="space-y-3">
              <ConsentRow label={getCopy(R, 'essential')} description="Required for login, security, and remembering your choice here." checked disabled />
              <ConsentRow label={getCopy(R, 'preferences2')} description="Remembers UI settings such as sidebar state and theme." checked={choices.preferences} onChange={(v) => setChoices((c) => ({ ...c, preferences: v }))} testId="consent-toggle-preferences" />
              <ConsentRow label={getCopy(R, 'analytics')} description="Aggregated usage metrics to help us improve PureBox." checked={choices.analytics} onChange={(v) => setChoices((c) => ({ ...c, analytics: v }))} testId="consent-toggle-analytics" />
              <ConsentRow label={getCopy(R, 'marketing')} description="Google Ads conversion measurement and future advertising features." checked={choices.marketing} onChange={(v) => setChoices((c) => ({ ...c, marketing: v }))} testId="consent-toggle-marketing" />
            </ul>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <button type="button" onClick={() => setMode('banner')} className="min-h-11 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50">
                {getCopy(R, 'back')}
              </button>
              <button type="button" onClick={onSavePreferences} data-testid="consent-save-preferences" className="min-h-11 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700">
                {getCopy(R, 'savePreferences')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConsentRow({ label, description, checked, onChange, disabled, testId }: {
  label: string
  description: string
  checked: boolean
  onChange?: (v: boolean) => void
  disabled?: boolean
  testId?: string
}) {
  return (
    <li className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 p-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <p className="text-sm text-slate-600">{description}</p>
      </div>
      <label className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-2">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} data-testid={testId} className="size-4 accent-blue-600 disabled:opacity-50" />
      </label>
    </li>
  );
}

export default CookieConsentBanner;
